/**
 * 线路择优模块
 *
 * 对多条备用线路并行测速，取最快成功的一条，缓存到 localStorage（4h 有效）。
 *
 * 跨实例并发锁（详见 wayfinder-lock.spec.md）：
 * 内存 stateMap 仅在同一模块实例内幂等，无法覆盖「多 SDK 各自打包」「多标签页」场景。
 * 故在 localStorage 上引入心跳锁：测速方每 1000ms 刷新心跳时间戳；其余实例读到活跃锁则
 * 轮询等待其结果，读到坏锁（心跳超过 2000ms 未刷新，通常因页面关闭/崩溃）则接管测速。
 * localStorage 无 CAS，初始抢锁 / 坏锁接管的偶发重复测速被明确容忍，最终结果收敛一致。
 *
 * API（签名与语义对调用方保持不变）:
 *  - startTest(serviceType)  主动触发测速（幂等）
 *  - getLine(serviceType)    获取最优线路（自动触发测速）
 *  - clearLine(serviceType)  清除结果并重新测速（尊重活跃锁）
 *  - getCurrentLine(serviceType)  同步获取当前线路
 */

import {
  readWayfinderCache,
  writeTestingLock,
  writeDoneResult,
  removeWayfinderCache,
  takeValidLine,
} from '../caches/wayfinder'
import type { WayfinderCache } from '../caches/wayfinder'
import { getServiceLineConfig } from '../config/service'
import type { ServiceType } from '../config/service'

// ─── 类型 ────────────────────────────────────────────

type Status = 'idle' | 'testing' | 'done'

interface WayfinderState {
  /** idle 空闲 / testing 进行中（自测或等待）/ done 已完成 */
  status: Status
  /** 进行中时持有的 promise，外部可 await 等待结果 */
  promise: Promise<string> | null
  /** promise 的 resolve，供测速完成 / 轮询到结果时结算 */
  resolve: ((line: string) => void) | null
  /** 测速完成后的最优线路 */
  result: string | null
  /** 测速方心跳定时器（1000ms 刷新锁时间戳） */
  heartbeatTimer: ReturnType<typeof setInterval> | null
  /** 等待方轮询定时器（200ms 读取锁状态） */
  pollTimer: ReturnType<typeof setInterval> | null
}

// ─── 状态管理 ─────────────────────────────────────────

const stateMap = new Map<ServiceType, WayfinderState>()

/** 取该 serviceType 的状态，不存在则初始化为 idle */
function ensureState(serviceType: ServiceType): WayfinderState {
  let state = stateMap.get(serviceType)
  if (!state) {
    state = { status: 'idle', promise: null, resolve: null, result: null, heartbeatTimer: null, pollTimer: null }
    stateMap.set(serviceType, state)
  }
  return state
}

/** 该 state 是否仍是 stateMap 中该 serviceType 的当前实例（识别 clearLine 后的孤儿回调） */
function isCurrent(serviceType: ServiceType, state: WayfinderState): boolean {
  return stateMap.get(serviceType) === state
}

/** 进入进行中态（自测或等待）：置 testing 并惰性创建 promise，保存 resolve 供后续结算 */
function beginProgress(state: WayfinderState) {
  state.status = 'testing'
  if (!state.promise) {
    state.promise = new Promise<string>((resolve) => {
      state.resolve = resolve
    })
  }
}

/** 清理该状态上的所有定时器 */
function clearTimers(state: WayfinderState) {
  if (state.heartbeatTimer) {
    clearInterval(state.heartbeatTimer)
    state.heartbeatTimer = null
  }
  if (state.pollTimer) {
    clearInterval(state.pollTimer)
    state.pollTimer = null
  }
}

/** 结算为完成态：停定时器、置结果、resolve 等待方。幂等，不写缓存（由调用方决定是否持久化）。 */
function settleDone(state: WayfinderState, line: string) {
  if (state.status === 'done') return // 已结算，忽略迟到回调
  clearTimers(state)
  state.status = 'done'
  state.result = line
  if (!state.promise) state.promise = Promise.resolve(line)
  state.resolve?.(line)
  state.resolve = null
}

/** 缓存已被别的实例写成 done 则采纳其线路并结算，返回是否已采纳 */
function adoptWinner(state: WayfinderState, raw: WayfinderCache | null): boolean {
  if (raw?.status === 'done' && raw.line) {
    settleDone(state, raw.line)
    return true
  }
  return false
}

// ─── 时间参数 ─────────────────────────────────────────

const PROBE_PATH = '/system/health'
const PROBE_TIMEOUT = 20_000
/** 测速方心跳刷新间隔 */
const HEARTBEAT_INTERVAL = 1_000
/** 坏锁阈值：心跳超过此时长未刷新即视为坏锁 */
const LOCK_STALE = 2_000
/** 等待方轮询间隔 */
const POLL_INTERVAL = 200

/** 该服务的完整线路列表（gateway + 备用线路，去空） */
function getAllLines(serviceType: ServiceType): string[] {
  const { gateway, backupLines } = getServiceLineConfig(serviceType)
  return [gateway, ...backupLines].filter(Boolean)
}

/** 缓存锁是否活跃：正在测速且心跳未超时 */
function isLockAlive(raw: WayfinderCache | null): boolean {
  return !!raw && raw.status === 'testing' && Date.now() - raw.timestamp <= LOCK_STALE
}

// ─── 测速 ─────────────────────────────────────────────

/**
 * 并行竞速所有线路，取最快成功返回的一条，其余请求 abort。
 * 有胜者 -> resolve 该线路；全部失败/超时 -> reject（由调用方兜底 gateway 并清锁）。
 */
async function runSpeedTest(serviceType: ServiceType): Promise<string> {
  const allLines = getAllLines(serviceType)
  const controllers = allLines.map(() => new AbortController())
  const timers = controllers.map((c) => setTimeout(() => c.abort(), PROBE_TIMEOUT))

  try {
    // 取首个成功线路；全部失败/超时则抛 AggregateError，由调用方 catch 兜底 gateway
    return await Promise.any(
      allLines.map((line, i) =>
        fetch(line + PROBE_PATH, { method: 'HEAD', signal: controllers[i]!.signal }).then((res) => {
          if (res.status >= 500) throw new Error(`${line} responded with ${res.status}`)
          return line
        }),
      ),
    )
  } finally {
    timers.forEach(clearTimeout)
    controllers.forEach((c) => c.abort()) // 胜者已定 / 全败：取消其余仍在进行的请求
  }
}

/** 成为测速方：写锁 + 起心跳 + 开测，成功写结果、失败清锁兜底 gateway。 */
function startTesting(serviceType: ServiceType, state: WayfinderState) {
  beginProgress(state)

  writeTestingLock(serviceType)
  state.heartbeatTimer = setInterval(() => {
    // 心跳前先看是否已有别的实例胜出：已 done 则采纳并停心跳，否则刷新锁
    if (adoptWinner(state, readWayfinderCache(serviceType))) return
    writeTestingLock(serviceType)
  }, HEARTBEAT_INTERVAL)

  runSpeedTest(serviceType)
    .then((line) => {
      // 孤儿回调（已被 clearLine 取代）或已结算 -> 不写缓存
      if (!isCurrent(serviceType, state) || state.status === 'done') return
      // 先看缓存：别的实例已胜出则采纳，避免 done 覆盖翻转（first-done-wins）
      if (adoptWinner(state, readWayfinderCache(serviceType))) return
      writeDoneResult(serviceType, line)
      settleDone(state, line)
    })
    .catch(() => {
      if (!isCurrent(serviceType, state) || state.status === 'done') return
      // 全部线路不可达：删除缓存 key（等待方读不到就自测），内存兜底 gateway
      removeWayfinderCache(serviceType)
      settleDone(state, getServiceLineConfig(serviceType).gateway)
    })
}

/** 成为等待方：轮询锁状态，直到读到结果 / 坏锁 / key 消失。 */
function becomeWaiter(serviceType: ServiceType, state: WayfinderState) {
  beginProgress(state)
  state.pollTimer = setInterval(() => pollOnce(serviceType, state), POLL_INTERVAL)
}

/** 等待方轮询一次，三种出口命中即停止轮询。 */
function pollOnce(serviceType: ServiceType, state: WayfinderState) {
  const raw = readWayfinderCache(serviceType)

  // 出口1：测速方已完成 -> 复用结果
  if (adoptWinner(state, raw)) return

  // 出口3：key 不存在（测速方失败已删除），或 done 但无 line（异常态）-> 自己测速
  if (!raw || raw.status === 'done') {
    clearTimers(state)
    startTesting(serviceType, state)
    return
  }

  // 出口2：坏锁（心跳超时）-> 接管测速
  if (!isLockAlive(raw)) {
    clearTimers(state)
    startTesting(serviceType, state)
    return
  }

  // 否则：锁仍活跃，继续等待
}

// ─── 公开 API ─────────────────────────────────────────

/** 主动触发测速。已在进行/完成则跳过（幂等）；存在活跃锁则等待，否则自测。 */
export function startTest(serviceType: ServiceType): void {
  const { backupLines } = getServiceLineConfig(serviceType)
  if (!backupLines.length) return

  const state = ensureState(serviceType)
  if (state.status === 'testing' || state.status === 'done') return

  // 复用有效的 done 结果（且线路仍在当前配置中，否则视为配置变更走重测）
  const validLine = takeValidLine(serviceType)
  if (validLine && getAllLines(serviceType).includes(validLine)) {
    state.status = 'done'
    state.result = validLine
    state.promise = Promise.resolve(validLine)
    return
  }

  // 存在活跃心跳锁 -> 等待；否则（无锁 / 坏锁 / 无效 done）-> 自测
  const raw = readWayfinderCache(serviceType)
  if (isLockAlive(raw)) {
    becomeWaiter(serviceType, state)
  } else {
    startTesting(serviceType, state)
  }
}

/** 获取最优线路。已有结果直接返回，进行中则等待，空闲则自动触发测速。 */
export function getLine(serviceType: ServiceType): Promise<string> {
  const { gateway, backupLines } = getServiceLineConfig(serviceType)
  if (!backupLines.length) return Promise.resolve(gateway)

  const state = ensureState(serviceType)

  if (state.status === 'done' && state.result) {
    return Promise.resolve(state.result)
  }

  if (state.status === 'testing' && state.promise) {
    return state.promise
  }

  startTest(serviceType)
  return ensureState(serviceType).promise!
}

/**
 * 清除测速结果并重新发起测速（用于线路故障自动恢复）。
 * 尊重活跃锁：若缓存正处于活跃测速中，不破坏缓存，仅重置本地状态后走 startTest 进入等待。
 */
export function clearLine(serviceType: ServiceType): void {
  const state = stateMap.get(serviceType)
  if (state) clearTimers(state)
  stateMap.delete(serviceType)

  // done 结果 / 坏锁 -> 清掉强制重测；活跃锁 -> 保留，startTest 会进入等待
  if (!isLockAlive(readWayfinderCache(serviceType))) {
    removeWayfinderCache(serviceType)
  }

  startTest(serviceType)
}

/** 同步获取当前使用的线路；无备用线路时返回 gateway，无测速结果时返回空字符串。 */
export function getCurrentLine(serviceType: ServiceType): string {
  const { gateway, backupLines } = getServiceLineConfig(serviceType)
  if (!backupLines.length) return gateway
  return stateMap.get(serviceType)?.result ?? ''
}
